/**
 * SDKStorage — the SDK's one durable-state seam (sdk ADR-011). Synchronous (the holder phase restores through `atomWithStorage`
 * with `getOnInit: true`; an async seam re-introduces the one-render-signed-in
 * hydration race), string-keyed, and ENUMERABLE — `keys()` is what makes the
 * teardown residue assertion ("no key survives under the SDK namespace that is
 * not classified") expressible at all.
 *
 * The SDK never sniffs the platform to pick a backing. Two adapters ship, both
 * real with production callers: Web `Storage` (browser hosts) and memory
 * (credential-less hosts and the test/story harness — a deliberate
 * non-durable choice, never a silent default: constructing a credential holder
 * without an explicit adapter throws at construction).
 */

import type { CredentialIdentity, CredentialMaterial } from '../auth/authState.js';

export const SDK_STORAGE_NAMESPACE = 'la:sdk:';

export interface SDKStorage {
  /** The STORE this adapter reads and writes, not the adapter instance. Two
   *  realms that resolve the same id share an origin's durable state, which is
   *  what lets the identity bus elide material and have the observer re-read it
   *  (sdk ADR-015). */
  readonly scopeId: string;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Every key currently present, namespaced or not — the residue gate
   *  enumerates this and filters on {@link SDK_STORAGE_NAMESPACE}. */
  keys(): string[];
}

/**
 * The persisted credential (sdk ADR-014). Storage holds
 * material, identity and expiry; the phase is DERIVED on restore.
 */
export interface PersistedCredential {
  version: number;
  material: CredentialMaterial;
  identity: CredentialIdentity;
  expires_at: number;
}

/** A blob written by a different schema version, or unparseable at this one. */
export type CredentialUnreadableCause = 'version_mismatch' | 'malformed';

export const PERSISTED_CREDENTIAL_VERSION = 2;
